import React, { createContext, useContext, useState, useEffect } from 'react';
import api, { resetSessionExpiredGuard, SESSION_EXPIRED_EVENT } from '../services/api';

const AuthContext = createContext(null);

// `skipSessionHandling: true` le dice al interceptor de `services/api.js` que un 401
// en esta llamada NO es una sesión que expiró (es un login/registro fallido, o un
// token viejo que se está verificando en silencio al abrir la app) — así que no debe
// disparar el modal global de "tu sesión expiró".
const SIN_MANEJO_SESION = { skipSessionHandling: true };

// El backend sigue siendo la única fuente real de autorización de superadmin
// (ver requireSuperAdmin en backend/routes/superadmin.js, que compara contra
// SUPERADMIN_EMAIL del lado del servidor). Esto es solo para decidir qué
// mostrar/ocultar en la UI (nav del sidebar, redirección de la ruta) — se
// calcula en un único lugar en vez de repetir la comparación de email en
// cada componente que necesite saberlo.
const SUPERADMIN_EMAIL = (process.env.REACT_APP_SUPERADMIN_EMAIL || '').trim().toLowerCase();

export function AuthProvider({ children }) {
  const [usuario, setUsuario] = useState(null);
  const [restaurante, setRestaurante] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [sesionExpirada, setSesionExpirada] = useState(false);

  useEffect(() => {
    verificarSesion();
  }, []);

  // Cuando cualquier request autenticada recibe 401, mostramos un aviso en vez de
  // navegar de inmediato. No limpiamos `usuario` aquí a propósito: así la pantalla
  // actual (y cualquier formulario en curso) sigue montada hasta que el usuario
  // confirme explícitamente que quiere ir a iniciar sesión de nuevo.
  useEffect(() => {
    const onSesionExpirada = () => setSesionExpirada(true);
    window.addEventListener(SESSION_EXPIRED_EVENT, onSesionExpirada);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onSesionExpirada);
  }, []);

  const verificarSesion = async () => {
    const token = localStorage.getItem('riq_token');
    if (!token) { setCargando(false); return; }
    try {
      const { data } = await api.get('/api/auth/me', SIN_MANEJO_SESION);
      setUsuario(data.usuario);
      setRestaurante(data.restaurante);
    } catch {
      localStorage.removeItem('riq_token');
    } finally {
      setCargando(false);
    }
  };

  const login = async (email, password) => {
    const { data } = await api.post('/api/auth/login', { email, password }, SIN_MANEJO_SESION);
    localStorage.setItem('riq_token', data.token);
    setUsuario(data.usuario);
    setRestaurante(data.restaurante);
    setSesionExpirada(false);
    resetSessionExpiredGuard();
    return data;
  };

  const registro = async (nombre, email, password, nombreRestaurante) => {
    const { data } = await api.post('/api/auth/registro', { nombre, email, password, nombreRestaurante }, SIN_MANEJO_SESION);
    localStorage.setItem('riq_token', data.token);
    setUsuario(data.usuario);
    setRestaurante(data.restaurante);
    setSesionExpirada(false);
    resetSessionExpiredGuard();
    return data;
  };

  const googleLogin = async (credential, nombreRestaurante) => {
    const { data } = await api.post('/api/auth/google', { credential, nombreRestaurante }, SIN_MANEJO_SESION);
    localStorage.setItem('riq_token', data.token);
    setUsuario(data.usuario);
    setRestaurante(data.restaurante);
    setSesionExpirada(false);
    resetSessionExpiredGuard();
    return data;
  };

  const logout = () => {
    localStorage.removeItem('riq_token');
    setUsuario(null);
    setRestaurante(null);
    setSesionExpirada(false);
    resetSessionExpiredGuard();
  };

  const esSuperadmin = Boolean(SUPERADMIN_EMAIL && usuario?.email && usuario.email.toLowerCase() === SUPERADMIN_EMAIL);

  return (
    <AuthContext.Provider value={{ usuario, restaurante, cargando, sesionExpirada, esSuperadmin, login, registro, googleLogin, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
